import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import {
  DOUBT_REVEAL_MS,
  DOUBT_SUSPENSE_MS,
  DOUBT_WINDOW_MS,
  SPECIAL_REVEAL_MS,
  SUITS,
  rankLabel,
  rankOf,
  suitOf,
  type Card,
  type ValeEvent,
} from '../../../shared/games/vale/rules.js';
import type { ValeView } from '../../../shared/games/vale/view.js';
import type { ArenaResult, MultiGameStateView } from '../../../shared/protocol.js';
import type { ArenaSocket } from '../../realtime.js';

export function PlayingCard(props: { card: Card; selected?: boolean; onClick?: () => void; small?: boolean; style?: Record<string, string> }) {
  const r = rankLabel(rankOf(props.card));
  const s = SUITS[suitOf(props.card)]!;
  const red = suitOf(props.card) === 1 || suitOf(props.card) === 2;
  const content = (
    <>
      <span class="pc-corner tl">
        {r}
        <br />
        {s}
      </span>
      <span class="pc-center">{s}</span>
      <span class="pc-corner br">
        {r}
        <br />
        {s}
      </span>
    </>
  );
  const cls = `pcard ${red ? 'red' : ''} ${props.selected ? 'selected' : ''} ${props.small ? 'small' : ''}`;
  return props.onClick ? (
    <button type="button" class={cls} style={props.style} onClick={props.onClick} aria-pressed={props.selected} aria-label={`${r}${s}`}>
      {content}
    </button>
  ) : (
    <span class={cls} style={props.style} aria-label={`${r}${s}`}>
      {content}
    </span>
  );
}

export const CardBack = (props: { style?: Record<string, string>; small?: boolean }) => <span class={`pcard back ${props.small ? 'small' : ''}`} style={props.style} aria-hidden="true" />;

/** Valepaskahousu UI (decision 2026-09-30): own hand, the table, claims and the "Liar!" button. */
export function ValeGame(props: { state: MultiGameStateView<ValeView>; socket: ArenaSocket; myId: number; serverNow: number }) {
  const { state, socket, myId, serverNow } = props;
  // A burning batch nobody doubted burns as soon as the doubt window is over (fix 2026-09-30):
  // the pile is shown gone at once, without waiting for the burner's next play.
  const raw = state.module;
  const burnedNow = raw.burnAt !== null && serverNow >= raw.burnAt;
  const v: ValeView = burnedNow ? { ...raw, pile: 0, top: null, run: 0, open: false, pendingBurn: null, burnAt: null } : raw;
  const [burnNote, setBurnNote] = useState<number | null>(null);
  const burnedBefore = useRef(false);
  // The note's own timer: it must not be cancelled when the burner plays on (fix 2026-09-30: the
  // note stayed on the table).
  const burnTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(burnTimer.current), []);
  useEffect(() => {
    if (burnedNow && !burnedBefore.current) {
      burnedBefore.current = true;
      playSound('valeBurn');
      setBurnNote(raw.pile);
      clearTimeout(burnTimer.current);
      burnTimer.current = setTimeout(() => setBurnNote(null), 2600);
    } else if (!burnedNow) {
      burnedBefore.current = false;
    }
  }, [burnedNow]);
  // The next play clears the note at once.
  useEffect(() => setBurnNote(null), [raw.seq]);
  const V = t.vale;
  const [selected, setSelected] = useState<Card[]>([]);
  const [claim, setClaim] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [shown, setShown] = useState<ValeEvent | null>(null);
  const [busy, setBusy] = useState(false);
  const name = (id: number) => (id === myId ? V.you : (state.players.find((p) => p.id === id)?.displayName ?? '?'));
  const myTurn = v.turn === myId;
  const meOut = v.players.find((p) => p.userId === myId)?.out ?? null;
  const waitMs = Math.max(0, v.playableAt - serverNow);

  // Keep the selection valid when the hand changes.
  useEffect(() => {
    setSelected((sel) => sel.filter((c) => v.myHand.includes(c)));
  }, [v.myHand.join(',')]);

  // A sensible default claim: the rank of the selected cards when allowed, else the lowest allowed.
  const selectedRanks = [...new Set(selected.map(rankOf))];
  const suggested = selectedRanks.length === 1 && v.allowed.includes(selectedRanks[0]!) ? selectedRanks[0]! : (v.allowed[0] ?? null);
  const chosenClaim = claim !== null && v.allowed.includes(claim) ? claim : suggested;
  useEffect(() => setClaim(null), [v.seq]);

  // New events: animate the latest one and play its sound.
  // A doubt is shown in two stages (user request 2026-09-30): "X doubts!" with the cards face down,
  // then after DOUBT_SUSPENSE_MS the cards are turned over for everyone. The server pauses the game
  // for the same time.
  const lastSeen = useRef(v.events.at(-1)?.seq ?? 0);
  const [stage, setStage] = useState<'suspense' | 'reveal'>('reveal');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    const fresh = v.events.filter((e) => (e.seq ?? 0) > lastSeen.current);
    if (fresh.length === 0) return;
    lastSeen.current = Math.max(...fresh.map((e) => e.seq ?? 0));
    const important = [...fresh].reverse().find((e) => e.kind !== 'out') ?? fresh[fresh.length - 1]!;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    const later = (ms: number, fn: () => void) => timers.current.push(setTimeout(fn, ms));
    setShown(important);
    if (important.kind === 'doubt') {
      setStage('suspense');
      playSound('challenge');
      for (let s = 1; s < DOUBT_SUSPENSE_MS / 1000; s++) later(s * 1000, () => playSound('turnTick'));
      later(DOUBT_SUSPENSE_MS, () => {
        setStage('reveal');
        playSound(important.truthful ? 'declined' : 'valeCaught');
      });
      later(DOUBT_REVEAL_MS, () => setShown(null));
      return;
    }
    setStage('reveal');
    if (important.kind === 'burn') playSound('valeBurn', 250);
    else if (important.kind === 'out') playSound('accepted');
    else playSound('cardFlip');
    later(important.kind === 'empty_special' ? SPECIAL_REVEAL_MS : 2400, () => setShown(null));
  }, [v.seq]);

  // A card lands on the pile.
  const pileBefore = useRef(v.pile);
  useEffect(() => {
    if (v.pile > pileBefore.current) playSound('cardFlip');
    pileBefore.current = v.pile;
  }, [v.pile]);

  const send = (payload: unknown) => {
    setBusy(true);
    setNotice(null);
    socket.emit('multi:move', payload, (r: ArenaResult) => {
      setBusy(false);
      if (!r.ok) setNotice(r.error === 'invalid_move' ? V.tooLate : t.arena.errors[r.error]);
    });
  };
  const play = () => {
    if (!chosenClaim || selected.length === 0) return;
    send({ type: 'play', cards: selected, claim: chosenClaim });
    setSelected([]);
  };
  const doubt = () => send({ type: 'doubt', seq: v.seq });
  const toggle = (c: Card) => setSelected((sel) => (sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c]));

  const canDoubt = v.open && v.top !== null && v.top.by !== myId && meOut === null && !busy;
  const eventText = (e: ValeEvent): string => {
    switch (e.kind) {
      case 'doubt':
        return e.truthful ? V.doubtTrue(name(e.doubter), name(e.accused), e.pileSize) : V.doubtFalse(name(e.doubter), name(e.accused), e.pileSize);
      case 'burn':
        return V.burn(name(e.by), V.burnReason[e.reason]);
      case 'empty_special':
        return e.truthful ? V.emptyTrue(name(e.by), e.receiver === null ? '?' : name(e.receiver)) : V.emptyFalse(name(e.by));
      case 'out':
        return V.out(name(e.userId), e.place);
    }
  };

  return (
    <div class="vale">
      <div class="vale-seats">
        {v.players.map((p) => {
          const player = state.players.find((x) => x.id === p.userId);
          return (
            <div class={`vale-seat ${v.turn === p.userId ? 'turn' : ''} ${p.out ? 'out' : ''} ${player?.dropped ? 'dropped' : ''}`}>
              <div class="vale-seat-name">
                {p.userId === myId ? V.you : player?.displayName}
                {player?.isAi && <span class="muted"> 🤖</span>}
              </div>
              {p.out ? (
                <div class="vale-seat-out">{p.out === 1 ? '🥇' : p.out === 2 ? '🥈' : p.out === 3 ? '🥉' : `${p.out}.`}</div>
              ) : (
                <div class="vale-seat-cards" aria-label={V.cardsLeft(p.cards)}>
                  {Array.from({ length: Math.min(p.cards, 8) }, (_, i) => (
                    <CardBack small style={{ transform: `rotate(${(i - Math.min(p.cards, 8) / 2) * 6}deg)` }} />
                  ))}
                  <span class="vale-count">{p.cards}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div class={`vale-table ${v.pendingBurn ? 'burning' : ''}`}>
        <div class="vale-deck" title={V.deck}>
          {v.deck > 0 ? <CardBack /> : <span class="pcard empty" />}
          <span class="vale-label">
            {V.deck}: {v.deck}
          </span>
        </div>
        <div class="vale-pile">
          {v.pile === 0 ? (
            <span class="pcard empty" />
          ) : (
            Array.from({ length: Math.min(v.pile, 6) }, (_, i) => (
              <CardBack style={{ transform: `translate(${i * 3}px, ${-i * 2}px) rotate(${((i * 37) % 17) - 8}deg)` }} />
            ))
          )}
          <span class="vale-label">
            {V.pile}: {v.pile}
          </span>
        </div>
        <div class="vale-claim" aria-live="polite">
          {v.top ? (
            <>
              <div class="vale-claim-who">{name(v.top.by)}:</div>
              <div class="vale-claim-what">{V.claim(v.top.count, rankLabel(v.top.claim))}</div>
              {v.run > v.top.count && <div class="muted">{V.run(v.run, rankLabel(v.top.claim))}</div>}
              {v.pendingBurn && <div class="vale-burn-note">🔥 {V.burning}</div>}
            </>
          ) : (
            <div class="muted">{V.emptyTable}</div>
          )}
        </div>
        {shown && shown.kind === 'doubt' && stage === 'suspense' ? (
          <div class="vale-event doubt suspense" role="status">
            <div class="vale-suspense-title">🔍 {V.doubts(name(shown.doubter))}</div>
            <div class="vale-reveal">
              {shown.cards.map(() => (
                <span class="vale-shake">
                  <CardBack />
                </span>
              ))}
            </div>
            <div class="vale-event-text">{V.doubtedClaim(name(shown.accused), shown.cards.length, rankLabel(shown.claim))}</div>
          </div>
        ) : (
          shown && (
            <div class={`vale-event ${shown.kind} ${shown.kind === 'doubt' ? (shown.truthful ? 'truth' : 'lie') : ''}`} role="status">
              {(shown.kind === 'doubt' || shown.kind === 'empty_special') && (
                <div class="vale-reveal">
                  {shown.cards.map((c, i) => (
                    <span class="vale-flip" style={{ animationDelay: `${i * 0.12}s` }}>
                      <PlayingCard card={c} />
                    </span>
                  ))}
                </div>
              )}
              {shown.kind === 'doubt' && <div class="vale-verdict">{shown.truthful ? V.truth : V.lie}</div>}
              <div class="vale-event-text">{eventText(shown)}</div>
            </div>
          )
        )}
        {burnNote !== null && !shown && (
          <div class="vale-event burn" role="status">
            <div class="vale-suspense-title">🔥 {V.burnedTitle}</div>
            <div class="vale-event-text">{V.burned(burnNote)}</div>
          </div>
        )}
      </div>

      {/* The doubt bar (user request 2026-09-30): big and red while the latest batch can be doubted. */}
      {canDoubt && v.top && (
        <div class="vale-doubt-bar" role="group" aria-label={V.liar}>
          <div class="vale-doubt-info">
            <strong>{V.claims(name(v.top.by), v.top.count, rankLabel(v.top.claim))}</strong>
            <span>{waitMs > 0 ? V.doubtWindow : V.doubtUntil(name(v.turn))}</span>
            <div class="vale-doubt-timer" aria-hidden="true">
              <div class="vale-doubt-timer-fill" style={{ width: `${Math.min(100, (waitMs / DOUBT_WINDOW_MS) * 100)}%` }} />
            </div>
          </div>
          <button class="vale-doubt-btn" onClick={doubt}>
            {V.doubtButton}
          </button>
        </div>
      )}

      {notice && <p class="error">{notice}</p>}

      {meOut === null && (
        <div class="vale-hand-area">
          <div class="vale-hand">
            {v.myHand.map((c, i) => (
              <PlayingCard card={c} selected={selected.includes(c)} onClick={() => toggle(c)} style={{ '--i': String(i - v.myHand.length / 2) }} />
            ))}
          </div>
          {myTurn && (
            <div class="vale-controls">
              <div class="vale-claims" role="group" aria-label={V.claimAs}>
                <span class="muted">{V.claimAs}:</span>
                {v.allowed.map((r) => (
                  <button type="button" class={`btn btn-small ${chosenClaim === r ? 'btn-primary' : ''}`} aria-pressed={chosenClaim === r} onClick={() => setClaim(r)}>
                    {rankLabel(r)}
                  </button>
                ))}
              </div>
              <button class="btn btn-primary vale-play" disabled={selected.length === 0 || !chosenClaim || waitMs > 0 || busy} onClick={play}>
                {waitMs > 0
                  ? V.wait(Math.ceil(waitMs / 1000))
                  : selected.length === 0
                    ? V.selectCards
                    : V.play(selected.length, rankLabel(chosenClaim ?? 0), selected.some((c) => rankOf(c) !== chosenClaim))}
              </button>
            </div>
          )}
        </div>
      )}
      {meOut !== null && <p class="vale-out-note">{V.youAreOut(meOut)}</p>}
    </div>
  );
}
